import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ctaBand } from '../content/copy';

/**
 * The small repeated shapes. Five screens use these; the styling lives in
 * global.css because they are as much a part of the seam as .btn is.
 *
 * They are grouped in one file rather than five because each is a handful of
 * lines with no state — five imports and five files would be more ceremony than
 * the components are worth.
 */

/** Title left, count right, 2px underline. Heads every itemised list. */
export function SectionHead({
  title,
  count,
  level = 3,
}: {
  title: string;
  count?: string;
  /** Bumped to 2 where the list is a top-level section rather than a column. */
  level?: 2 | 3;
}) {
  const Heading = level === 2 ? 'h2' : 'h3';
  return (
    <div className="section-head">
      <Heading className="section-head__title">{title}</Heading>
      {count && <span className="section-head__count">{count}</span>}
    </div>
  );
}

/** Label left, figure right. Payout rows, key dates. */
export function DataRow({
  label,
  value,
  lead = false,
}: {
  label: ReactNode;
  value: ReactNode;
  /** Red figure — the headline row of its group. */
  lead?: boolean;
}) {
  return (
    <div className={lead ? 'data-row data-row--lead' : 'data-row'}>
      <span className="data-row__label">{label}</span>
      <span className="data-row__value">{value}</span>
    </div>
  );
}

export function StatCard({
  value,
  label,
  detail,
}: {
  value: ReactNode;
  label: string;
  detail?: string;
}) {
  return (
    <div className="stat-card">
      <div className="stat-card__value">{value}</div>
      <div className="stat-card__label">{label}</div>
      {detail && <p className="stat-card__detail">{detail}</p>}
    </div>
  );
}

export function BorderBox({
  label,
  tone = 'default',
  note,
  children,
}: {
  label: string;
  /** Amber marks the entry fee — the one figure that is fixed and knowable. */
  tone?: 'default' | 'gold' | 'soft';
  note?: string;
  children: ReactNode;
}) {
  const toneClass =
    tone === 'gold' ? ' border-box--gold' : tone === 'soft' ? ' border-box--soft' : '';
  return (
    <div className={`border-box${toneClass}`}>
      <div className="border-box__label">{label}</div>
      {children}
      {note && <p className="border-box__note">{note}</p>}
    </div>
  );
}

/**
 * The closing band. Every screen but the landing page ends on it.
 *
 * The whole band is swappable, not just the heading, because on /prizes the
 * right closing action depends on who you turned out to be — and telling a
 * paid-up member to "register your interest" is worse than saying nothing.
 */
export function CtaBand({
  heading = ctaBand.heading,
  body = ctaBand.body,
  flourish = ctaBand.flourish,
  label = ctaBand.label,
  sub = ctaBand.sub,
  to = '/join',
}: {
  heading?: string;
  body?: string;
  /** null suppresses it — the registered and member variants both pass null. */
  flourish?: string | null;
  label?: string;
  sub?: string;
  to?: string;
}) {
  return (
    <section className="wrap wrap--wide cta-band">
      <div className="cta-band__copy">
        <h2>{heading}</h2>
        <p>{body}</p>
        {flourish && <p className="cta-band__flourish">{flourish}</p>}
      </div>
      <Link to={to} className="btn-hero">
        <span className="btn-hero__label">{label}</span>
        <span className="btn-hero__sub">{sub}</span>
      </Link>
    </section>
  );
}
